import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './DetectorInfo.css';

const DetectorInfo: React.FC = () => {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const detectors = [
    {
      name: 'GPTZero',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      description: 'Analisa a perplexidade (previsibilidade das palavras) e burstiness (variação no comprimento das frases). Textos com padrões muito uniformes e baixa variação tendem a ser identificados como IA.',
      focus: 'Perplexidade e Variação'
    },
    {
      name: 'Turnitin',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      description: 'Utiliza análise estilométrica com mais de 30 características linguísticas. Examina diversidade lexical (palavras únicas vs. repetidas), transições formais e uniformidade no comprimento das frases.',
      focus: 'Diversidade Lexical e Estilo'
    },
    {
      name: 'ZeroGPT',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      description: 'Foca em padrões estruturais e regularidade excessiva. Detecta palavras formais típicas de IA como "utilize", "facilitate" e "optimal". Textos muito perfeitos na estrutura são marcados como suspeitos.',
      focus: 'Padrões e Regularidade'
    },
    {
      name: 'Copyleaks',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      description: 'Usa detecção reversa focando no que NÃO é humano. Procura por frases excessivamente formais como "it is important to note" e ausência de linguagem informal. Marcadores pessoais (eu acho, sinto) reduzem pontuação de IA.',
      focus: 'Detecção Reversa'
    },
    {
      name: 'QuillBot',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      description: 'Especializado em detectar conteúdo parafraseado por IA. Analisa uniformidade no comprimento de frases, linguagem genérica e padrões repetitivos usando trigramas (sequências de 3 palavras). Alta repetição indica IA.',
      focus: 'Paráfrase e Uniformidade'
    },
    {
      name: 'Grammarly',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      description: 'Examina sintaxe e estrutura gramatical. Gramática perfeita demais sugere IA. Detecta uso excessivo de voz passiva (comum em IA) e prefere estruturas simples. Marcadores conversacionais (tipo, sabe) indicam escrita humana.',
      focus: 'Sintaxe e Gramática'
    }
  ];

  return (
    <div className="detector-info-page">
      <div className="detector-info-container">
        <div className="detector-info-header">
          <h1>Como Funcionam os Detectores de IA</h1>
          <p className="detector-info-subtitle">
            Nossa análise combina 6 detectores líderes do mercado, cada um usando diferentes heurísticas para identificar conteúdo gerado por IA.
          </p>
        </div>

        <div className="detectors-grid">
          {detectors.map((detector, index) => (
            <div key={index} className="detector-card">
              <div className="detector-card-header">
                <div className="detector-icon">{detector.icon}</div>
                <div>
                  <h3 className="detector-name">{detector.name}</h3>
                  <span className="detector-focus">{detector.focus}</span>
                </div>
              </div>
              <p className="detector-description">{detector.description}</p>
            </div>
          ))}
        </div>

        <div className="analysis-note">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <div>
            <h4>Como Calculamos a Pontuação Final</h4>
            <p>
              Cada detector analisa o texto individualmente e gera uma pontuação de 0-100%.
              A pontuação final que você vê é a média de todos os 6 detectores,
              proporcionando uma análise mais robusta e precisa do que usar apenas um detector.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetectorInfo;
